import { useCallback, useRef, useState } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';

interface ReferenceImageDropProps {
  /** Currently-selected reference image (Blob) — or null when empty. */
  value: Blob | null;
  /** Fired when the user picks/drops a new image, or clears via the X. */
  onChange: (blob: Blob | null) => void;
  /** Header label, shown above the drop zone (e.g. "Background reference"). */
  label: string;
  /** Optional disambiguation hint shown below the drop zone. */
  hint?: string;
  /** Mirrors the parent panel's busy state — disables interaction while
   *  a generation is in flight. */
  disabled?: boolean;
  /** MIME prefix accepted (default `image/`). */
  accept?: string;
}

/**
 * Compact reference-image input used by AI sub-panels that accept an
 * optional source image alongside a prompt (Replace Background's BG
 * reference + light reference, etc.). Returns a Blob via `onChange`;
 * the panel converts to data URL at submit time.
 *
 * Two ways to set:
 *   • Click the zone → opens a native file picker.
 *   • Drag a file from the OS over the zone → drops it in.
 *
 * Once set, the zone shows a thumbnail (object URL, revoked on unmount
 * or replacement) and an X button to clear. The component intentionally
 * does NOT integrate with the FiasFiles picker — that's a heavier
 * dialog and worth deferring to a later iteration.
 */
export function ReferenceImageDrop({
  value,
  onChange,
  label,
  hint,
  disabled = false,
  accept = 'image/',
}: ReferenceImageDropProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Object URL for the thumbnail preview. Revoked when the value
  // changes or the component unmounts so we don't leak blob URLs.
  const objectUrlRef = useRef<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Keep `previewUrl` in sync with `value` and revoke any prior URL on
  // change. Inline render-phase sync (rather than useEffect) is fine
  // here because the work is purely creating/revoking blob URLs and
  // setting state — both idempotent. Using useEffect would push the
  // preview one render behind the parent's `value` prop, producing a
  // visible flash of the empty state when re-mounting.
  if (value && !previewUrl) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(value);
    objectUrlRef.current = url;
    setPreviewUrl(url);
  } else if (!value && previewUrl) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setPreviewUrl(null);
  }

  const handleFile = useCallback(
    (file: File | null) => {
      if (!file) return;
      if (!file.type.startsWith(accept)) return;
      onChange(file);
    },
    [onChange, accept],
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleClear = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onChange(null);
      if (inputRef.current) inputRef.current.value = '';
    },
    [onChange, disabled],
  );

  return (
    <div className="space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={handleClick}
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files[0];
          if (file) handleFile(file);
        }}
        disabled={disabled}
        className={`relative w-full aspect-[4/1] rounded-md border border-dashed ${
          isDragOver ? 'border-primary bg-accent' : 'border-border bg-background'
        } flex items-center justify-center text-xs text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none overflow-hidden`}
      >
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt="Reference preview"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear reference image"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e);
                }
              }}
              className="absolute top-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-background/90 hover:bg-background border border-border"
            >
              <X className="w-3 h-3" />
            </span>
          </>
        ) : (
          <span className="flex items-center gap-2 px-3">
            <ImageIcon className="w-4 h-4" />
            <span>Drop or click to pick an image</span>
          </span>
        )}
      </button>
      {hint && <p className="text-[11px] text-muted-foreground leading-snug">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={`${accept}*`}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
